/* eslint-disable */
import { Currency, CurrencyAmount, Percent, TradeType } from '@uniswap/sdk-core'
import { Route, SwapQuoter } from '@uniswap/v3-sdk'
import { useAccount } from 'hooks/useAccount'
import { UniverseChainId } from 'uniswap/src/types/chains'
import JSBI from 'jsbi'
import { useSingleContractMultipleData, useSingleContractWithCallData } from '../../lib/hooks/multicall'
import { useMemo } from 'react'
import { ApproveInfo, SubmittableTrade, TradeState } from 'state/routing/types'

import { ClassicTrade, QuoteMethod } from 'state/routing/types'

import { isCelo, isTrustk } from 'constants/tokens'
import { useAllV3Routes } from './trustkeys/useAllV3Routes'
import { useQuoter } from '../../hooks/useContract'
import { getUSDCostPerGas } from './utils'
import { getApproveInfo } from './gas'


const QUOTE_GAS_OVERRIDES: { [chainId: number]: number } = {
  [UniverseChainId.ArbitrumOne]: 25_000_000,
  [UniverseChainId.ArbitrumGoerli]: 25_000_000,
  [UniverseChainId.Celo]: 50_000_000,
  [UniverseChainId.CeloAlfajores]: 50_000_000,
  [UniverseChainId.Polygon]: 40_000_000,
  [UniverseChainId.PolygonMumbai]: 40_000_000,
  [UniverseChainId.Bnb]: 50_000_000,
  [UniverseChainId.Trustkeys]: 50_000_000,
}

const DEFAULT_GAS_QUOTE = 2_000_000

/**
 * Returns the best v3 trade for a desired swap
 * @param tradeType whether the swap is an exact in/out
 * @param amountSpecified the exact amount to swap in/out
 * @param otherCurrency the desired output/payment currency
 */

export function useClientSideV3Trade<TTradeType extends TradeType>(
  tradeType: TTradeType,
  amountSpecified?: CurrencyAmount<Currency> | undefined,
  otherCurrency?: Currency | undefined,
  account?: string,
) : { 
  state: TradeState
  trade?: SubmittableTrade
  currentTrade?: SubmittableTrade
  swapQuoteLatency?: number 
} {
  const [currencyIn, currencyOut] =
    tradeType === TradeType.EXACT_INPUT
      ? [amountSpecified?.currency, otherCurrency]
      : [otherCurrency, amountSpecified?.currency]
  const { routes, loading: routesLoading } = useAllV3Routes(currencyIn, currencyOut)

  const { chainId } = useAccount()
  // Chains deployed using the deploy-v3 script only deploy QuoterV2.
  const useQuoterV2 = useMemo(() => Boolean(chainId && isCelo(chainId)), [chainId])
  const quoter = useQuoter(useQuoterV2)

  const callData = useMemo(
    () =>
      amountSpecified
        ? routes.map(
            (route) => SwapQuoter.quoteCallParameters(route, amountSpecified, tradeType, { useQuoterV2 }).calldata
          )
        : [],
    [amountSpecified, routes, tradeType, useQuoterV2]
  )
  // console.log(' call data:', callData)
  // const swapFee = {
  //   recipient: '0x246BbaC3b0Cb5e7bF137880EB771AA9c87cD5698',
  //   percent: new Percent(25,10000),
  //   amount:"8828286"
  // }


  const quotesResults = useSingleContractWithCallData(quoter, callData, {
    gasRequired: chainId ? QUOTE_GAS_OVERRIDES[chainId] ?? DEFAULT_GAS_QUOTE : undefined,
  })

  // console.log('Quote results:',quotesResults)

  const currenciesAreTheSame = useMemo(
    () => currencyIn && currencyOut && (currencyIn.equals(currencyOut) || currencyIn.wrapped.equals(currencyOut)),
    [currencyIn, currencyOut]
  )

  return useMemo(() => {
    if (
      !amountSpecified ||
      !currencyIn ||
      !currencyOut ||
      quotesResults.some(({ valid }) => !valid) ||
      currenciesAreTheSame
    ) {
      return {
        state: TradeState.INVALID,
        trade: undefined,
      }
    }

    if (routesLoading || quotesResults.some(({ loading }) => loading)) {
      return {
        state: TradeState.LOADING,
        trade: undefined,
      }
    }

    const { bestRoute, amountIn, amountOut } = quotesResults.reduce(
      (
        currentBest: {
          bestRoute: Route<Currency, Currency> | null
          amountIn: CurrencyAmount<Currency> | null
          amountOut: CurrencyAmount<Currency> | null
        },
        { result },
        i
      ) => {
        if (!result) return currentBest

        // overwrite the current best if it's not defined or if this route is better
        if (tradeType === TradeType.EXACT_INPUT) {
          const amountOut = CurrencyAmount.fromRawAmount(currencyOut, result.amountOut.toString())
          if (currentBest.amountOut === null || JSBI.lessThan(currentBest.amountOut.quotient, amountOut.quotient)) {
            return {
              bestRoute: routes[i],
              amountIn: amountSpecified,
              amountOut,
            }
          }
        } else {
          const amountIn = CurrencyAmount.fromRawAmount(currencyIn, result.amountIn.toString())
          if (currentBest.amountIn === null || JSBI.greaterThan(currentBest.amountIn.quotient, amountIn.quotient)) {
            return {
              bestRoute: routes[i],
              amountIn,
              amountOut: amountSpecified,
            }
          }
        }

        return currentBest
      },
      {
        bestRoute: null,
        amountIn: null,
        amountOut: null,
      }
    )

    if (!bestRoute || !amountIn || !amountOut) {
      return {
        state: TradeState.NO_ROUTE_FOUND,
        trade: undefined,
      }
    }
    const gasUseEstimateUSD = 0.007798223358378449
    const gasUseEstimate = 50_000
    const usdCostPerGas = getUSDCostPerGas(gasUseEstimateUSD, gasUseEstimate)
    let approveInfo : ApproveInfo = { needsApprove: false }
    async () =>{
      approveInfo = await getApproveInfo(account, currencyIn, amountSpecified.quotient.toString(), usdCostPerGas)
    } 

    // let approveInfo : ApproveInfo = { needsApprove: false }
    // if (currencyIn.isNative) {
    //   approveInfo = { needsApprove: false }
    // }
    // else if (!account || !usdCostPerGas) {
    //   approveInfo = { needsApprove: false }
    // } else {

    //   approveInfo = { needsApprove: true, approveGasEstimateUSD: 65000 * usdCostPerGas }
    // }
    
    // const swapFee = {
    //   recipient: '0x246BbaC3b0Cb5e7bF137880EB771AA9c87cD5698',
    //   percent: new Percent(25,10000),
    //   amount: /*'3604538'*/ BigInt(amountSpecified)
    // }
    const swapFeeUndefind = {
      recipient: '0x246BbaC3b0Cb5e7bF137880EB771AA9c87cD5698',
      percent: new Percent(25,10000),
      amount: '1'
    }

    console.log('routev3 trade:',bestRoute)
    return {
      state: TradeState.VALID,
      trade: new ClassicTrade({
        v2Routes: [],
        v3Routes: [
          {
            routev3: bestRoute,
            inputAmount: amountIn,
            outputAmount: amountOut,
          },
        ],
        mixedRoutes: [],
        tradeType,
        gasUseEstimateUSD : gasUseEstimateUSD,
        gasUseEstimate : gasUseEstimate,
        approveInfo: approveInfo,
        blockNumber : '692633',
        // requestId:  '',
        quoteMethod : QuoteMethod.CLIENT_SIDE_FALLBACK,
        swapFee: swapFeeUndefind,
      }),
      currentTrade: undefined,
      swapQuoteLatency: 2000000,
    }
  }, [
    amountSpecified, 
    currenciesAreTheSame, 
    currencyIn, 
    currencyOut, 
    quotesResults, 
    routes, 
    routesLoading, 
    tradeType
  ])
}
